import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ExpandedState,
  type PaginationState,
  type RowSelectionState,
  type ColumnFiltersState,
  type ColumnSizingState,
  type VisibilityState,
  type ColumnOrderState,
  type OnChangeFn,
} from '@tanstack/react-table';
import { useTheme } from '@/theme';
import { Button, Flex, Spinner } from '@/components/ui';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import * as XLSX from 'xlsx';
import { Pagination } from './Pagination';

interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, any>[];

  // 페이지네이션
  enablePagination?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];

  // 정렬
  enableSorting?: boolean;

  // Row 확장
  enableExpanding?: boolean;
  getRowCanExpand?: (row: any) => boolean;
  renderSubComponent?: (row: any) => React.ReactNode;

  // Row 스타일
  getRowStyle?: (row: any) => React.CSSProperties;

  // Row 선택
  enableRowSelection?: boolean;
  onRowSelectionChange?: (selectedRows: TData[]) => void;

  // Row 클릭
  onRowClick?: (row: TData) => void;

  // 로딩 상태
  isLoading?: boolean;

  // 빈 상태 메시지
  emptyMessage?: string;

  // 스타일 커스터마이징
  className?: string;
  tableStyle?: React.CSSProperties;

  // 엑셀 다운로드
  enableExcelExport?: boolean;
  excelFileName?: string;
  onExcelExportReady?: (exportFn: () => void) => void;

  // 필터링
  enableFiltering?: boolean;
  enableGlobalFilter?: boolean;
  globalFilter?: string;
  onGlobalFilterChange?: (filter: string) => void;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;

  // 컬럼 리사이징
  enableColumnResizing?: boolean;
  columnResizeMode?: 'onChange' | 'onEnd';
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: OnChangeFn<ColumnSizingState>;

  // 컬럼 표시/숨기기
  enableColumnVisibility?: boolean;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;

  // 컬럼 순서 변경
  enableColumnOrdering?: boolean;
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: OnChangeFn<ColumnOrderState>;
}

export function DataTable<TData>({
  data,
  columns,
  enablePagination = false,
  pageSize = 10,
  pageSizeOptions = [10, 20, 50, 100],
  enableSorting = true,
  enableExpanding = false,
  getRowCanExpand,
  renderSubComponent,
  enableRowSelection = false,
  onRowSelectionChange,
  onRowClick,
  isLoading = false,
  emptyMessage = 'No data available',
  className,
  tableStyle,
  getRowStyle,
  enableExcelExport = false,
  excelFileName = 'data',
  onExcelExportReady,
  enableFiltering = false,
  enableGlobalFilter = false,
  globalFilter: controlledGlobalFilter,
  onGlobalFilterChange,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange,
  enableColumnResizing = false,
  columnResizeMode = 'onEnd',
  columnSizing: controlledColumnSizing,
  onColumnSizingChange,
  enableColumnVisibility = false,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange,
  enableColumnOrdering = false,
  columnOrder: controlledColumnOrder,
  onColumnOrderChange,
}: DataTableProps<TData>) {
  const { theme } = useTheme();
  const { t } = useTranslation('common');

  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [internalGlobalFilter, setInternalGlobalFilter] = useState<string>('');
  const [internalColumnFilters, setInternalColumnFilters] = useState<ColumnFiltersState>([]);
  const [internalColumnSizing, setInternalColumnSizing] = useState<ColumnSizingState>({});
  const [internalColumnVisibility, setInternalColumnVisibility] = useState<VisibilityState>({});
  const [internalColumnOrder, setInternalColumnOrder] = useState<ColumnOrderState>([]);

  // 헤더 높이를 동적으로 측정하기 위한 ref와 state
  // sticky 필터 행이 헤더 바로 아래에 정확히 위치하도록 실제 렌더링된 헤더 높이를 측정
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const [headerHeight, setHeaderHeight] = useState(44); // 초기값은 대략적인 높이

  // 필터링 상태 관리 (controlled/uncontrolled)
  const globalFilter =
    controlledGlobalFilter !== undefined ? controlledGlobalFilter : internalGlobalFilter;
  const columnFilters =
    controlledColumnFilters !== undefined ? controlledColumnFilters : internalColumnFilters;
  const columnSizing =
    controlledColumnSizing !== undefined ? controlledColumnSizing : internalColumnSizing;
  const columnVisibility =
    controlledColumnVisibility !== undefined
      ? controlledColumnVisibility
      : internalColumnVisibility;
  const columnOrder =
    controlledColumnOrder !== undefined ? controlledColumnOrder : internalColumnOrder;

  const handleGlobalFilterChange = (value: string) => {
    if (onGlobalFilterChange) {
      onGlobalFilterChange(value);
    } else {
      setInternalGlobalFilter(value);
    }
  };

  const handleColumnFiltersChange: OnChangeFn<ColumnFiltersState> = updater => {
    if (onColumnFiltersChange) {
      onColumnFiltersChange(updater);
    } else {
      setInternalColumnFilters(
        typeof updater === 'function'
          ? (updater as (prev: ColumnFiltersState) => ColumnFiltersState)(internalColumnFilters)
          : updater
      );
    }
  };

  const handleColumnSizingChange: OnChangeFn<ColumnSizingState> = updater => {
    if (onColumnSizingChange) {
      onColumnSizingChange(updater);
    } else {
      setInternalColumnSizing(
        typeof updater === 'function'
          ? (updater as (prev: ColumnSizingState) => ColumnSizingState)(internalColumnSizing)
          : updater
      );
    }
  };

  const handleColumnVisibilityChange: OnChangeFn<VisibilityState> = updater => {
    if (onColumnVisibilityChange) {
      onColumnVisibilityChange(updater);
    } else {
      setInternalColumnVisibility(
        typeof updater === 'function'
          ? (updater as (prev: VisibilityState) => VisibilityState)(internalColumnVisibility)
          : updater
      );
    }
  };

  const handleColumnOrderChange: OnChangeFn<ColumnOrderState> = updater => {
    if (onColumnOrderChange) {
      onColumnOrderChange(updater);
    } else {
      setInternalColumnOrder(
        typeof updater === 'function'
          ? (updater as (prev: ColumnOrderState) => ColumnOrderState)(internalColumnOrder)
          : updater
      );
    }
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      expanded,
      rowSelection,
      pagination: enablePagination ? pagination : undefined,
      columnFilters: enableFiltering ? columnFilters : undefined,
      globalFilter: enableGlobalFilter ? globalFilter : undefined,
      columnSizing: enableColumnResizing ? columnSizing : undefined,
      columnVisibility: enableColumnVisibility ? columnVisibility : undefined,
      columnOrder: enableColumnOrdering ? columnOrder : undefined,
    },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: enablePagination ? setPagination : undefined,
    onColumnFiltersChange: enableFiltering ? handleColumnFiltersChange : undefined,
    onGlobalFilterChange: enableGlobalFilter ? handleGlobalFilterChange : undefined,
    onColumnSizingChange: enableColumnResizing ? handleColumnSizingChange : undefined,
    onColumnVisibilityChange: enableColumnVisibility ? handleColumnVisibilityChange : undefined,
    onColumnOrderChange: enableColumnOrdering ? handleColumnOrderChange : undefined,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: enableFiltering || enableGlobalFilter ? getFilteredRowModel() : undefined,
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    getExpandedRowModel: enableExpanding ? getExpandedRowModel() : undefined,
    getRowCanExpand: enableExpanding ? getRowCanExpand : undefined,
    enableSorting,
    enableExpanding,
    enableRowSelection,
    enableColumnResizing,
    columnResizeMode,
    globalFilterFn: enableGlobalFilter ? 'includesString' : undefined,
  });

  // 헤더 높이 동적 측정
  // ResizeObserver를 사용하여 헤더 높이 변경을 감지하고 필터 행의 sticky top 위치를 업데이트
  useEffect(() => {
    if (!headerRowRef.current || !enableFiltering) return;

    const updateHeaderHeight = () => {
      if (headerRowRef.current) {
        const height = headerRowRef.current.offsetHeight;
        setHeaderHeight(height);
      }
    };

    // 초기 높이 측정
    updateHeaderHeight();

    // ResizeObserver로 헤더 높이 변경 감지
    const resizeObserver = new ResizeObserver(updateHeaderHeight);
    resizeObserver.observe(headerRowRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [enableFiltering, columns, data.length]);

  // Row 선택 변경 시 콜백 호출
  React.useEffect(() => {
    if (onRowSelectionChange && enableRowSelection) {
      const selectedRows = table.getSelectedRowModel().rows.map(row => row.original);
      onRowSelectionChange(selectedRows);
    }
  }, [rowSelection, onRowSelectionChange, enableRowSelection, table]);

  // 엑셀 다운로드 함수
  const handleExcelExport = useCallback(() => {
    // 컬럼 헤더 추출 (actions 같은 특수 컬럼 제외)
    const headers = columns
      .filter(col => {
        const id = (col as any).id || (col as any).accessorKey;
        return id && id !== 'actions' && id !== 'expander';
      })
      .map(col => {
        const header = col.header;
        if (typeof header === 'string') return header;
        if (typeof header === 'function') {
          // 함수형 헤더는 실행해서 텍스트 추출 시도
          try {
            const result = header({} as any);
            return typeof result === 'string' ? result : (col as any).accessorKey || 'Column';
          } catch {
            return (col as any).accessorKey || 'Column';
          }
        }
        return (col as any).accessorKey || 'Column';
      });

    // 데이터 추출
    const rows = data.map(row => {
      const rowData: any = {};
      columns.forEach((col, index) => {
        const id = (col as any).id || (col as any).accessorKey;
        if (id && id !== 'actions' && id !== 'expander') {
          const accessorKey = (col as any).accessorKey;
          if (accessorKey) {
            const value = accessorKey.split('.').reduce((obj: any, key: string) => obj?.[key], row);
            rowData[headers[index]] = Array.isArray(value) ? value.join(', ') : (value ?? '');
          } else if (col.cell && typeof col.cell === 'function') {
            // cell 함수가 있는 경우 실행해서 값 추출 시도
            try {
              const cellValue = col.cell({ row: { original: row } } as any);
              rowData[headers[index]] =
                typeof cellValue === 'string' || typeof cellValue === 'number' ? cellValue : '';
            } catch {
              rowData[headers[index]] = '';
            }
          }
        }
      });
      return rowData;
    });

    // 워크시트 생성
    const worksheet = XLSX.utils.json_to_sheet(rows);

    // 워크북 생성
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    // 파일 다운로드
    const fileName = `${excelFileName}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }, [data, columns, excelFileName]);

  // Excel export 함수를 외부로 노출
  React.useEffect(() => {
    // console.log('DataTable: onExcelExportReady effect running', {
    //   hasCallback: !!onExcelExportReady,
    //   hasExportFn: !!handleExcelExport,
    //   dataLength: data.length
    // });
    if (onExcelExportReady) {
      // console.log('DataTable: Calling onExcelExportReady with export function');
      onExcelExportReady(handleExcelExport);
    }
  }, [onExcelExportReady, handleExcelExport]);

  const textStyle = {
    color: theme.colors.text.primary,
    fontSize: `${theme.fontSize.sm}px`,
  };

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      {/* 전역 필터 입력 */}
      {enableGlobalFilter && (
        <Flex
          justify="start"
          align="center"
          style={{ marginBottom: `${theme.spacing[4]}px`, flexShrink: 0 }}
        >
          <input
            type="text"
            value={globalFilter}
            onChange={e => handleGlobalFilterChange(e.target.value)}
            placeholder={t(i18nKeys.common.search)}
            style={{
              padding: `${theme.spacing[2]}px ${theme.spacing[4]}px`,
              borderRadius: `${theme.borderRadius.sm}px`,
              border: `1px solid ${theme.colors.border.container}`,
              backgroundColor: theme.colors.surface.layout.overlay,
              color: theme.colors.text.primary,
              fontSize: `${theme.fontSize.sm}px`,
              width: '300px',
            }}
          />
        </Flex>
      )}

      {/* 컬럼 표시/숨기기 및 엑셀 다운로드 */}
      {(enableColumnVisibility || enableExcelExport) && (
        <Flex
          justify="between"
          align="center"
          style={{ marginBottom: `${theme.spacing[4]}px`, flexShrink: 0 }}
        >
          {/* 컬럼 표시/숨기기 메뉴 */}
          {/* {enableColumnVisibility && (
            <div style={{ position: 'relative' }}>
              <Button
                variant="secondary"
                size="sm"
                onClick={e => {
                  e.stopPropagation();
                  const menu = document.getElementById('column-visibility-menu');
                  if (menu) {
                    menu.style.display = menu.style.display === 'none' ? 'block' : 'none';
                  }
                }}
              >
                <span>👁️</span>
                <span>{t(i18nKeys.common.columnVisibility)}</span>
              </Button>
              <div
                id="column-visibility-menu"
                style={{
                  display: 'none',
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: `${theme.spacing[2]}px`,
                  backgroundColor: theme.colors.surface.layout.overlay,
                  border: `1px solid ${theme.colors.border.container}`,
                  borderRadius: `${theme.borderRadius.sm}px`,
                  padding: `${theme.spacing[4]}px`,
                  zIndex: 1000,
                  minWidth: '200px',
                  boxShadow: `0 4px 6px rgba(0, 0, 0, 0.1)`,
                }}
                onClick={e => e.stopPropagation()}
              >
                {table.getAllColumns().map(column => {
                  if (column.id === 'actions' || column.id === 'expander') return null;
                  return (
                    <label
                      key={column.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: `${theme.spacing[2]}px`,
                        padding: `${theme.spacing[2]}px`,
                        cursor: 'pointer',
                        fontSize: `${theme.fontSize.sm}px`,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={column.getIsVisible()}
                        onChange={e => {
                          column.toggleVisibility(e.target.checked);
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>
                        {typeof column.columnDef.header === 'string'
                          ? column.columnDef.header
                          : column.id}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )} */}
        </Flex>
      )}

      {/* 테이블 */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            ...tableStyle,
          }}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <React.Fragment key={headerGroup.id}>
                {/* 헤더 행 - ref를 통해 실제 높이를 측정하여 필터 행의 sticky 위치 계산에 사용 */}
                <tr ref={headerRowRef}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      style={{
                        padding: `${theme.spacing[3]}px ${theme.spacing[2]}px`,
                        textAlign: 'center',
                        borderBottom: 'none',
                        backgroundColor: theme.colors.surface.group.default,
                        fontWeight: theme.fontWeight.semibold,
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        userSelect: 'none',
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        ...(enableColumnResizing
                          ? header.getSize()
                            ? { width: header.getSize() }
                            : {}
                          : header.column.columnDef.size
                            ? { width: header.column.columnDef.size }
                            : {}),
                        ...(header.column.columnDef.minSize
                          ? { minWidth: header.column.columnDef.minSize }
                          : {}),
                        ...(header.column.columnDef.maxSize
                          ? { maxWidth: header.column.columnDef.maxSize }
                          : {}),
                        ...((header.column.columnDef.meta as any)?.style || {}),
                        ...textStyle,
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: `${theme.spacing[2]}px`,
                          position: 'relative',
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span style={{ opacity: 0.5 }}>
                            {{
                              asc: ' ↑',
                              desc: ' ↓',
                            }[header.column.getIsSorted() as string] ?? ' ⇅'}
                          </span>
                        )}
                      </div>
                      {enableColumnResizing && header.column.getCanResize() && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            height: '100%',
                            width: '5px',
                            cursor: 'col-resize',
                            userSelect: 'none',
                            touchAction: 'none',
                            backgroundColor: header.column.getIsResizing()
                              ? theme.colors.button.bg.primary.default
                              : theme.colors.button.bg.primary.default,
                            zIndex: 3,
                          }}
                          onMouseEnter={e => {
                            if (!header.column.getIsResizing()) {
                              (e.target as HTMLElement).style.backgroundColor =
                                theme.colors.border.container;
                            }
                          }}
                          onMouseLeave={e => {
                            if (!header.column.getIsResizing()) {
                              (e.target as HTMLElement).style.backgroundColor = 'transparent';
                            }
                          }}
                        />
                      )}
                    </th>
                  ))}
                </tr>
                {/* 필터 행 - sticky 위치를 동적으로 측정된 헤더 높이에 맞춰 설정 */}
                {enableFiltering && (
                  <tr>
                    {headerGroup.headers.map(header => {
                      return (
                        <th
                          key={`filter-${header.id}`}
                          style={{
                            padding: `${theme.spacing[2]}px ${theme.spacing[2]}px`,
                            textAlign: 'center',
                            borderBottom: 'none',
                            backgroundColor: theme.colors.surface.group.default,
                            position: 'sticky',
                            // 동적으로 측정된 헤더 높이를 사용하여 필터 행이 헤더 바로 아래에 위치하도록 함
                            top: `${headerHeight}px`,
                            zIndex: 1,
                            ...((header.column.columnDef.meta as any)?.filterStyle || {}),
                          }}
                          onClick={e => e.stopPropagation()}
                        >
                          {header.column.getCanFilter() ? (
                            <input
                              type="text"
                              value={(header.column.getFilterValue() as string) ?? ''}
                              onChange={e => header.column.setFilterValue(e.target.value)}
                              placeholder={t(i18nKeys.common.filter)}
                              style={{
                                width: '100%',
                                padding: `${theme.spacing[2]}px ${theme.spacing[4]}px`,
                                borderRadius: `${theme.borderRadius.sm}px`,
                                border: `1px solid ${theme.colors.border.container}`,
                                backgroundColor: theme.colors.surface.layout.background,
                                color: theme.colors.text.primary,
                                fontSize: `${theme.fontSize.sm}px`,
                                boxSizing: 'border-box',
                              }}
                              onClick={e => e.stopPropagation()}
                            />
                          ) : (
                            <div style={{ height: '100%', minHeight: '32px' }} />
                          )}
                        </th>
                      );
                    })}
                  </tr>
                )}
              </React.Fragment>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    padding: `${theme.spacing[8]}px`,
                    textAlign: 'center',
                  }}
                >
                  <Flex align="center" justify="center">
                    <Spinner size="md" />
                  </Flex>
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    padding: `${theme.spacing[8]}px`,
                    textAlign: 'center',
                    ...textStyle,
                    color: theme.colors.text.secondary,
                  }}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map(row => {
                const customRowStyle = getRowStyle ? getRowStyle(row) : {};
                const baseRowStyle = {
                  backgroundColor: row.getIsSelected()
                    ? `${theme.colors.button.bg.primary}10`
                    : 'transparent',
                  cursor:
                    onRowClick || (enableExpanding && row.getCanExpand()) ? 'pointer' : 'default',
                };

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      style={{
                        ...baseRowStyle,
                        ...customRowStyle,
                      }}
                      onClick={() => {
                        if (onRowClick) {
                          onRowClick(row.original);
                        }
                        if (enableExpanding && row.getCanExpand()) {
                          row.toggleExpanded();
                        }
                      }}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          style={{
                            padding: `${theme.spacing[6]}px`,
                            borderBottom: `1px solid ${theme.colors.border.container}`,
                            ...(enableColumnResizing
                              ? cell.column.getSize()
                                ? { width: cell.column.getSize() }
                                : {}
                              : cell.column.columnDef.size
                                ? { width: cell.column.columnDef.size }
                                : {}),
                            ...((cell.column.columnDef.meta as any)?.style || {}),
                            ...textStyle,
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                    {row.getIsExpanded() && renderSubComponent && (
                      <tr>
                        <td
                          colSpan={columns.length}
                          style={{
                            padding: 0,
                            backgroundColor: theme.colors.surface.layout.background,
                            borderBottom: `1px solid ${theme.colors.border.container}`,
                          }}
                        >
                          {renderSubComponent(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {enablePagination && !isLoading && data.length > 0 && (
        <Pagination table={table} pageSizeOptions={pageSizeOptions} />
      )}

      {/* 엑셀 다운로드 버튼 (DownloadButtons 패턴) */}
      {enableExcelExport && (
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: theme.spacing[3],
            marginTop: theme.spacing[3],
          }}
        >
          <Button
            variant="primary"
            onClick={handleExcelExport}
            disabled={data.length === 0}
            style={{ width: '178px' }}
          >
            Download Excel
          </Button>
        </div>
      )}
    </div>
  );
}
